/**
 * Web Bluetooth Adapter
 *
 * Uses the Web Bluetooth API for connecting to thermal printers.
 * Works in Chrome, Edge, and Opera browsers.
 *
 * Note: Requires HTTPS in production.
 */

import type { PrinterAdapter, PrinterDevice } from '../types'

// Common Bluetooth service UUIDs for thermal printers
const PRINTER_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer service
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Common thermal printer service
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Another common printer service
]

// Characteristic UUIDs for writing to printer
const WRITE_CHARACTERISTIC_UUIDS = [
  '00002af1-0000-1000-8000-00805f9b34fb', // Generic write
  '49535343-8841-43f4-a8d4-ecbe34729bb3', // Common write characteristic
]

export class WebBluetoothAdapter implements PrinterAdapter {
  private device: BluetoothDevice | null = null
  private server: BluetoothRemoteGATTServer | null = null
  private writeCharacteristic: BluetoothRemoteGATTCharacteristic | null = null
  private connectedDevice: PrinterDevice | null = null

  /**
   * Check if Web Bluetooth is supported
   */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator
  }

  /**
   * Scan for available Bluetooth printers
   * Note: Web Bluetooth doesn't support background scanning.
   * This will prompt the user with a device picker.
   */
  async scan(): Promise<PrinterDevice[]> {
    if (!WebBluetoothAdapter.isSupported()) {
      console.warn('[WebBluetooth] Web Bluetooth not supported')
      return []
    }

    try {
      const bluetooth = navigator.bluetooth
      if (!bluetooth) {
        console.warn('[WebBluetooth] Web Bluetooth not supported')
        return []
      }
      const device = await bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUIDS,
      })

      if (device) {
        return [{
          id: device.id,
          name: device.name || 'Unknown Printer',
          type: 'bluetooth',
          connected: device.gatt?.connected || false,
        }]
      }

      return []
    } catch (error) {
      if ((error as Error).name === 'NotFoundError') {
        console.log('[WebBluetooth] User cancelled device selection')
        return []
      }
      console.error('[WebBluetooth] Scan error:', error)
      throw error
    }
  }

  /**
   * Connect to a printer by device ID
   */
  async connect(deviceId: string): Promise<boolean> {
    if (!WebBluetoothAdapter.isSupported()) {
      throw new Error('Web Bluetooth not supported')
    }

    try {
      if (this.device?.id === deviceId) {
        return this.connectToDevice(this.device)
      }

      const bluetooth = navigator.bluetooth
      if (!bluetooth) {
        throw new Error('Web Bluetooth not supported')
      }
      const device = await bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUIDS,
      })

      if (device.id !== deviceId) {
        throw new Error('Selected device does not match requested device')
      }

      return this.connectToDevice(device)
    } catch (error) {
      console.error('[WebBluetooth] Connect error:', error)
      return false
    }
  }

  /**
   * Internal method to connect to a BluetoothDevice
   */
  private async connectToDevice(device: BluetoothDevice): Promise<boolean> {
    try {
      this.device = device

      device.addEventListener('gattserverdisconnected', this.handleDisconnect)

      this.server = await device.gatt?.connect() || null

      if (!this.server) {
        throw new Error('Failed to connect to GATT server')
      }

      this.writeCharacteristic = await this.findWriteCharacteristic()

      if (!this.writeCharacteristic) {
        throw new Error('Could not find write characteristic')
      }

      this.connectedDevice = {
        id: device.id,
        name: device.name || 'Unknown Printer',
        type: 'bluetooth',
        connected: true,
      }

      console.log('[WebBluetooth] Connected to:', device.name)
      return true
    } catch (error) {
      console.error('[WebBluetooth] Connection failed:', error)
      await this.disconnect()
      return false
    }
  }

  /**
   * Find the write characteristic by trying known UUIDs
   */
  private async findWriteCharacteristic(): Promise<BluetoothRemoteGATTCharacteristic | null> {
    if (!this.server) return null

    for (const serviceUuid of PRINTER_SERVICE_UUIDS) {
      try {
        const service = await this.server.getPrimaryService(serviceUuid)

        for (const charUuid of WRITE_CHARACTERISTIC_UUIDS) {
          try {
            const characteristic = await service.getCharacteristic(charUuid)
            if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
              console.log('[WebBluetooth] Found write characteristic:', charUuid)
              return characteristic
            }
          } catch {
            // Characteristic not found, try next
          }
        }

        const characteristics = await service.getCharacteristics()
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            console.log('[WebBluetooth] Found writable characteristic:', char.uuid)
            return char
          }
        }
      } catch {
        // Service not found, try next
      }
    }

    try {
      const services = await this.server.getPrimaryServices()
      for (const service of services) {
        try {
          const characteristics = await service.getCharacteristics()
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              console.log('[WebBluetooth] Found writable characteristic in service:', service.uuid)
              return char
            }
          }
        } catch {
          // Continue to next service
        }
      }
    } catch (error) {
      console.error('[WebBluetooth] Error enumerating services:', error)
    }

    return null
  }

  /**
   * Handle disconnection events
   */
  private handleDisconnect = () => {
    console.log('[WebBluetooth] Device disconnected')
    this.server = null
    this.writeCharacteristic = null
    if (this.connectedDevice) {
      this.connectedDevice.connected = false
    }
  }

  /**
   * Disconnect from the current printer
   */
  async disconnect(): Promise<void> {
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', this.handleDisconnect)
    }

    if (this.server?.connected) {
      this.server.disconnect()
    }

    this.device = null
    this.server = null
    this.writeCharacteristic = null
    this.connectedDevice = null
  }

  /**
   * Check if currently connected
   */
  async isConnected(): Promise<boolean> {
    return this.server?.connected || false
  }

  /**
   * Get the currently connected device
   */
  getConnectedDevice(): PrinterDevice | null {
    if (this.connectedDevice && this.server?.connected) {
      return { ...this.connectedDevice, connected: true }
    }
    return null
  }

  /**
   * Send data to the printer
   */
  async print(data: Uint8Array): Promise<boolean> {
    if (!this.writeCharacteristic) {
      throw new Error('Not connected to a printer')
    }

    try {
      const mtuSize = 20

      for (let i = 0; i < data.length; i += mtuSize) {
        const chunk = data.slice(i, i + mtuSize)

        if (this.writeCharacteristic.properties.writeWithoutResponse) {
          await this.writeCharacteristic.writeValueWithoutResponse(chunk)
        } else {
          await this.writeCharacteristic.writeValueWithResponse(chunk)
        }

        if (i + mtuSize < data.length) {
          await new Promise(resolve => setTimeout(resolve, 20))
        }
      }

      console.log('[WebBluetooth] Print successful, sent', data.length, 'bytes')
      return true
    } catch (error) {
      console.error('[WebBluetooth] Print error:', error)
      return false
    }
  }
}
